import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import '../styles/GoogleDrivePicker.css';

const GoogleDrivePicker = ({ onSelect, selectedFile }) => {
  const [authorized, setAuthorized] = useState(false);
  const [needsReauth, setNeedsReauth] = useState(false);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');

  // Check authorization on mount
  useEffect(() => {
    checkAuthorization();
  }, []);

  // Fetch files when authorized
  useEffect(() => {
    if (authorized) {
      fetchFiles();
    }
  }, [authorized]);

  const checkAuthorization = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/api/sources/google-drive/check-auth', {}, {
        withCredentials: true
      });

      if (response.data.authorized) {
        setAuthorized(true);
        setNeedsReauth(false);
      } else {
        setAuthorized(false);
        setNeedsReauth(response.data.needs_reauth || false);
      }
    } catch (err) {
      console.error('Error checking Drive authorization:', err);
      setAuthorized(false);
      setNeedsReauth(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get('/api/sources/google-drive/files', {
        params: { page_size: 50 },
        withCredentials: true
      });

      if (response.data.success) {
        setFiles(response.data.files || []);
      } else {
        setError(response.data.error || 'Failed to load Drive files');
      }
    } catch (err) {
      console.error('Error fetching Drive files:', err);
      setError(err.response?.data?.error || 'Failed to load Drive files');
    } finally {
      setLoading(false);
    }
  };

  const handleReauthorize = async () => {
    // Log out first to clear existing session, then redirect to OAuth
    try {
      await axios.post('/auth/logout', {}, { withCredentials: true });
    } catch (err) {
      console.error('Error logging out:', err);
    }
    // Redirect to Google OAuth login after logout
    window.location.href = '/auth/google';
  };

  const handleFileSelect = (file) => {
    onSelect(file);
  };

  const getFileIcon = (mimeType) => {
    if (mimeType === 'application/vnd.google-apps.document') {
      return 'doc';
    } else if (mimeType === 'application/vnd.google-apps.spreadsheet') {
      return 'table';
    } else if (mimeType === 'application/vnd.google-apps.presentation') {
      return 'picture';
    } else if (mimeType === 'application/pdf') {
      return 'doc-text';
    }
    return 'doc';
  };

  const getFileTypeName = (mimeType) => {
    if (mimeType === 'application/vnd.google-apps.document') {
      return 'Google Doc';
    } else if (mimeType === 'application/vnd.google-apps.spreadsheet') {
      return 'Google Sheet';
    } else if (mimeType === 'application/vnd.google-apps.presentation') {
      return 'Google Slides';
    } else if (mimeType === 'application/pdf') {
      return 'PDF';
    }
    return 'Document';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredFiles = filterType === 'all'
    ? files
    : files.filter(file => {
        if (filterType === 'docs') return file.mimeType === 'application/vnd.google-apps.document';
        if (filterType === 'sheets') return file.mimeType === 'application/vnd.google-apps.spreadsheet';
        if (filterType === 'slides') return file.mimeType === 'application/vnd.google-apps.presentation';
        if (filterType === 'pdf') return file.mimeType === 'application/pdf';
        return true;
      });

  if (loading) {
    return (
      <div className="drive-picker-loading">
        <div className="loading-spinner"></div>
        <p>Loading Google Drive...</p>
      </div>
    );
  }

  if (!authorized || needsReauth) {
    return (
      <div className="drive-picker-unauthorized">
        <Icon name="google" size="3rem" />
        <h3>Connect to Google Drive</h3>
        <p>
          To import documents from Google Drive, you need to grant access.
          {needsReauth && ' You\'ll be logged out and asked to sign in again with updated permissions.'}
        </p>
        <button
          className="drive-auth-button"
          onClick={handleReauthorize}
        >
          <Icon name="google" size="1rem" />
          {needsReauth ? 'Sign Out & Re-authorize' : 'Connect Google Drive'}
        </button>
      </div>
    );
  }

  return (
    <div className="drive-picker-container">
      <div className="drive-picker-header">
        <h4>Select a file from Google Drive</h4>
      </div>

      <div className="drive-filter-tabs">
        <button
          className={`drive-filter-tab ${filterType === 'all' ? 'active' : ''}`}
          onClick={() => setFilterType('all')}
        >
          All
        </button>
        <button
          className={`drive-filter-tab ${filterType === 'docs' ? 'active' : ''}`}
          onClick={() => setFilterType('docs')}
        >
          <Icon name="doc" size="0.9rem" /> Docs
        </button>
        <button
          className={`drive-filter-tab ${filterType === 'sheets' ? 'active' : ''}`}
          onClick={() => setFilterType('sheets')}
        >
          <Icon name="table" size="0.9rem" /> Sheets
        </button>
        <button
          className={`drive-filter-tab ${filterType === 'slides' ? 'active' : ''}`}
          onClick={() => setFilterType('slides')}
        >
          <Icon name="picture" size="0.9rem" /> Slides
        </button>
        <button
          className={`drive-filter-tab ${filterType === 'pdf' ? 'active' : ''}`}
          onClick={() => setFilterType('pdf')}
        >
          <Icon name="doc-text" size="0.9rem" /> PDFs
        </button>
        <button
          className="drive-refresh-button"
          onClick={fetchFiles}
          disabled={loading}
        >
          <Icon name="refresh" size="0.9rem" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="drive-picker-error">
          {error}
        </div>
      )}

      {filteredFiles.length === 0 ? (
        <div className="drive-picker-empty">
          <Icon name="doc" size="2rem" />
          <p>No files found</p>
        </div>
      ) : (
        <div className="drive-file-list">
          {filteredFiles.map((file) => (
            <div
              key={file.id}
              className={`drive-file-item ${selectedFile?.id === file.id ? 'selected' : ''}`}
              onClick={() => handleFileSelect(file)}
            >
              <div className="drive-file-icon">
                <Icon name={getFileIcon(file.mimeType)} size="1.5rem" />
              </div>
              <div className="drive-file-info">
                <div className="drive-file-name">{file.name}</div>
                <div className="drive-file-meta">
                  {getFileTypeName(file.mimeType)} • Modified {formatDate(file.modifiedTime)}
                </div>
              </div>
              {selectedFile?.id === file.id && (
                <div className="drive-file-check">
                  <Icon name="checkmark" size="1.2rem" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {selectedFile && (
        <div className="drive-selected-info">
          <Icon name="checkmark" size="1rem" />
          Selected: <strong>{selectedFile.name}</strong>
        </div>
      )}
    </div>
  );
};

export default GoogleDrivePicker;
